import { FormProvider, useForm } from "react-hook-form";
import styles from "./styles.module.scss";
import utilStyles from "../utilStyles.module.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  dayHours,
  generateHoursWithLabels,
  useSettingsValues,
} from "../../../utils/timeUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  attendance_buttons,
  cancellation_buttons,
  generateCurrencySymbol,
  registration_close_buttons,
  registration_open_buttons,
} from "./utils";
import StateDayPicker from "../../../components/Programs/StateDayPicker";
import SelectBox from "../../../components/OptionButtonGroup/SelectBox";
import FormInput from "../../../components/Programs/FormInput";
import Label from "../../../components/Programs/Label";
import ButtonsContainer from "../../../components/Programs/Buttons/ButtonsContainer";
import SaveBar from "../../Sessions/SaveBar";
import { ProgramState } from "../../../reducers/scheduling/program";
import { RootState } from "typedefs";
import { isEmpty, isNull } from "lodash";
import { setPage } from "../../../actions/scheduling/program";
import {
  schedulingFetchPatch,
  schedulingFetchPost,
} from "../../../utils/request/scheduling/fetch";
import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import InformationModal from "app/components/Programs/InformationModal";
import { useTranslation } from "react-i18next";
import upload from "app/utils/uploader";
import CalendarX from "../../../../public/images/calendar-x-red.svg";
import { StylesConfig } from "react-select";
import StripeAccountSelector from "./StripeAccountSelector";
import PaymentMethodSelector from "app/components/PaymentMethodSelector";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import { FREE_CLINICS_ENABLED } from "../featureFlags";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { isAcademyOnboardingCoach } from "app/utils/subscription";

const generatePriceLabel = (currency: string, type: string, t) => {
  const base = type === 'Series' ? t('Total Series Price') : t('Price');
  const upperCurrency = currency?.toUpperCase();
  const showCurrencyCode = upperCurrency && upperCurrency !== generateCurrencySymbol(currency);

  return showCurrencyCode ? `${base} (${upperCurrency})` : base;
};

const PricingOptions = ({
  thumbnail,
  header,
  flyer,
  headerChanged,
  thumbnailChanged,
  flyerChanged,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { cut_off_days, cut_off_list } = useSettingsValues();
  const rolling_time_units = [
    { value: "days", label: t("Days Before") },
    { value: "weeks", label: t("Weeks Before") },
  ];
  const dispatch = useDispatch();
  const pricingState = useSelector(
    (state: RootState): ProgramState => state.program,
  );

  const currencyLabel = pricingState?.edit
    ? pricingState?.currency
    : generateCurrencySymbol(pricingState?.currency);
  const canEditRegistrationSettings = pricingState?.canEditRegistrationSettings ?? true;
  const canExtendRegistrationEndTime = pricingState?.canExtendRegistrationEndTime ?? false;
  const canEditRegistrationCloseDate = canEditRegistrationSettings || canExtendRegistrationEndTime;
  // CN-2564: a program whose registration closed can still get a new open date
  // until it starts, so dates saved in the past by mistake can be fixed.
  const canEditRegistrationOpenDate =
    canEditRegistrationSettings || (pricingState?.canEditRegistrationDates ?? false);
  const registrationCloseMinDate = useMemo(() => {
    if (canEditRegistrationSettings || !canExtendRegistrationEndTime) {
      return null;
    }
    const now = new Date();
    const currentClose = pricingState?.registrationCloseDate
      ? DateTime.fromISO(pricingState.registrationCloseDate).toJSDate()
      : null;
    if (currentClose && currentClose > now) {
      return currentClose;
    }
    return now;
  }, [canEditRegistrationSettings, canExtendRegistrationEndTime, pricingState?.registrationCloseDate]);
  const currentFacility = useCurrentFacility();
  const schedulingInterval = currentFacility?.data?.featureFlags?.schedulingInterval || 15;
  const { preferences } = useCalendarPreferences();
  const timeFormat = preferences.timeFormat;
  const hoursWithLables = useMemo(
    () => generateHoursWithLabels(schedulingInterval, timeFormat),
    [schedulingInterval, timeFormat],
  );
  const hasFacilityLevelStripe = currentFacility.data?.featureFlags?.facilityLevelStripeIntegration === true;
  const canEditPaymentCollector = pricingState?.canEditPaymentCollector ?? true;
  const canEditPaymentMethod = pricingState?.canEditPaymentMethod ?? true;
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [apiErrorText, setApiErrorText] = useState("");
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const methods = useForm({
    defaultValues: {
      capacity: pricingState.capacity,
      price: pricingState.price,
      stripe_account_id: pricingState.paymentCollectorCoachProfileId || null,
      rollingDaysBeforeOpen: pricingState?.registrationStartTimeOffset?.timeCount?.toString() || "1",
      rollingDaysBeforeClose: pricingState?.registrationEndTimeOffset?.timeCount?.toString() || "1",
    },
  });

  // Initialize payment collection states from Redux (for edit mode)
  const initializePaymentStates = () => {
    if (pricingState.collectType) {
      const isOnline = pricingState.collectType === 'online' || pricingState.collectType === 'both';
      const isOffline = pricingState.collectType === 'offline' || pricingState.collectType === 'both';
      return {
        collectOnline: isOnline,
        collectOffline: isOffline,
        onlineDefault: pricingState.onlineDefault || false,
      };
    }
    return { collectOnline: false, collectOffline: true, onlineDefault: false };
  };

  const initialStates = initializePaymentStates();
  const [collectOffline, setCollectOffline] = useState(initialStates.collectOffline);
  const [collectOnline, setCollectOnline] = useState(initialStates.collectOnline);
  const [onlineDefault, setOnlineDefault] = useState(initialStates.onlineDefault);
  const [isFree, setIsFree] = useState<boolean>(pricingState.isFree ?? false);
  const findTimeUnitOption = (unit) =>
    rolling_time_units.find((option) => option.value === unit) || rolling_time_units[0];

  const openOffset = pricingState?.registrationStartTimeOffset;
  const closeOffset = pricingState?.registrationEndTimeOffset;

  const [registrationOpenOption, setRegistrationOpenOption] = useState(
    openOffset ? registration_open_buttons()[1] : registration_open_buttons()[0],
  );
  const [registrationCloseOption, setRegistrationCloseOption] = useState(
    closeOffset ? registration_close_buttons()[1] : registration_close_buttons()[0],
  );
  const [rollingDaysBeforeOpen, setRollingDaysBeforeOpen] = useState(
    openOffset?.timeCount?.toString() || "1",
  );
  const [rollingTimeUnitOpen, setRollingTimeUnitOpen] = useState(
    findTimeUnitOption(openOffset?.timeUnit),
  );
  const [rollingDaysBeforeClose, setRollingDaysBeforeClose] = useState(
    closeOffset?.timeCount?.toString() || "1",
  );
  const [rollingTimeUnitClose, setRollingTimeUnitClose] = useState(
    findTimeUnitOption(closeOffset?.timeUnit),
  );

  const allCoachOptions = React.useMemo(() => {
    const list = Array.isArray(pricingState?.coaches) ? pricingState.coaches : [];

    return list.map((c: any) => ({
      id: c.id,
      value: c.id,
      label: c.fullName || `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim(),
      profilePicture: c.profilePicture || c.avatar,
      allowsPayments: c?.allowsPayments === true,
    }));
  }, [pricingState?.coaches]);

  const stripeEnabledCoaches = React.useMemo(() => {
    return allCoachOptions.filter((c) => c.allowsPayments);
  }, [allCoachOptions]);
  const self = useSelector((state: RootState) => state.user.self);
  // Academy onboarding analytics only, and only once the facility has loaded:
  // the Stripe status below reads facility flags, and the selector reports
  // the field once, so a first render before the facility arrives would
  // record it as unconnected for good.
  const paymentTracking =
    isAcademyOnboardingCoach(self) && !currentFacility.pending
      ? {
          objectType: "program" as const,
          // Facility Stripe when that flag is on, else any coach who takes payments.
          stripeConnected: hasFacilityLevelStripe
            ? Boolean(currentFacility.data.stripeIntegrationEnabled)
            : stripeEnabledCoaches.length > 0,
        }
      : undefined;

  useEffect(() => {
    if (!collectOffline && !collectOnline) {
      setCollectOffline(true);
    }
  }, [collectOffline, collectOnline]);

  useEffect(() => {
    if (!pricingState.edit) {
      const todayISO = DateTime.local().toISODate();
      if (!pricingState.registrationOpenDate) { setRegistrationOpenDate(todayISO); }
      if (!pricingState.registrationCloseDate) { setRegistrationCloseDate(todayISO); }
    }
  }, [pricingState.edit, pricingState.registrationOpenDate, pricingState.registrationCloseDate]);

  // Watch rolling input values for validation
  const rollingDaysBeforeOpenValue = methods.watch("rollingDaysBeforeOpen");
  const rollingDaysBeforeCloseValue = methods.watch("rollingDaysBeforeClose");

  // Clear payment collector if they're no longer in the coach list
  useEffect(() => {
    const currentCollector = methods.getValues('stripe_account_id');
    if (currentCollector && allCoachOptions.length > 0) {
      const isStillInList = allCoachOptions.some((coach) => coach.id === currentCollector);
      if (!isStillInList) {
        methods.setValue('stripe_account_id', null);
      }
    }
  }, [allCoachOptions, methods]);

  // Validate rollingDaysBeforeOpen when value changes
  useEffect(() => {
    if (registrationOpenOption.value === 1 && rollingDaysBeforeOpenValue !== undefined) {
      const numValue = parseInt(rollingDaysBeforeOpenValue, 10);
      if (isNaN(numValue) || numValue <= 0) {
        setFormErrors((prev) => ({ ...prev, rollingDaysBeforeOpen: true }));
      } else {
        setFormErrors((prev) => ({ ...prev, rollingDaysBeforeOpen: false }));
      }
      setRollingDaysBeforeOpen(rollingDaysBeforeOpenValue);
      methods.setValue('rollingDaysBeforeOpen', rollingDaysBeforeOpenValue);
    }
  }, [rollingDaysBeforeOpenValue, registrationOpenOption, methods]);

  // Validate rollingDaysBeforeClose when value changes
  useEffect(() => {
    if (registrationCloseOption.value === 1 && rollingDaysBeforeCloseValue !== undefined) {
      const numValue = parseInt(rollingDaysBeforeCloseValue, 10);
      if (isNaN(numValue) || numValue <= 0) {
        setFormErrors((prev) => ({ ...prev, rollingDaysBeforeClose: true }));
      } else {
        setFormErrors((prev) => ({ ...prev, rollingDaysBeforeClose: false }));
      }
      setRollingDaysBeforeClose(rollingDaysBeforeCloseValue);
      methods.setValue('rollingDaysBeforeClose', rollingDaysBeforeCloseValue);
    }
  }, [rollingDaysBeforeCloseValue, registrationCloseOption, methods]);

  const isOnlineDefault =
    (collectOnline && !collectOffline) ||
    (collectOnline && collectOffline && onlineDefault);

  const [registrationOpenTime, setRegistrationOpenTime] = useState(
    pricingState.registrationOpenTime,
  );
  const [registrationCloseTime, setRegistrationCloseTime] = useState(
    pricingState.registrationCloseTime,
  );
  const [registrationOpenDate, setRegistrationOpenDate] = useState(
    pricingState.registrationOpenDate,
  );
  const [registrationCloseDate, setRegistrationCloseDate] = useState(
    pricingState.registrationCloseDate,
  );
  const attendanceOptionSelector = () => {
    if (pricingState.waitlist || isNull(pricingState.waitlist)) {
      return attendance_buttons()[0];
    }
    return attendance_buttons()[1];
  };
  const [attendanceOption, setAttendanceOption] = useState(
    attendanceOptionSelector(),
  );
  const [cancellationOption, setCancellationOption] = useState(
    pricingState.cutOffType === "static"
      ? cancellation_buttons()[1]
      : cancellation_buttons()[0],
  );
  const [cutOffDay, setCutOffDay] = useState(
    pricingState.cutOffDay || {
      value: 24,
      label: t("count_day", { count: 1 }),
    },
  );
  const [cutOffTime, setCutOffTime] = useState(
    pricingState.cutOffTime || {
      value: 0,
      label: "12:00 AM",
    },
  );
  const [formErrors, setFormErrors] = useState({
    registrationOpenTime: false,
    registrationCloseTime: false,
    registrationOpenDate: false,
    registrationCloseDate: false,
    registrationOpenDatePast: false,
    registrationCloseDatePast: false,
    start_time: false,
    static: false,
    rollingDaysBeforeOpen: false,
    rollingTimeUnitOpen: false,
    rollingDaysBeforeClose: false,
    rollingTimeUnitClose: false,
  });

  const [loading, setLoading] = useState(false);

  const errorMessage = t("This field is required");

  const handleDateChange = (value, label) => {
    label === "open"
      ? setRegistrationOpenDate(value)
      : setRegistrationCloseDate(value);
  };

  const handleTimeChange = (time, label) => {
    label === "registrationOpen"
      ? setRegistrationOpenTime(time)
      : setRegistrationCloseTime(time);
  };

  const handleCutOffButtonChange = (value) => {
    setCancellationOption(value);
    value.value === 0
      ? setCutOffDay({ value: 0, label: t("Day Of") })
      : setCutOffDay({ value: 24, label: t("1 Day") });
  };

  const handleCutOffChange = (value, label) => {
    label === "static" ? setCutOffDay(value) : setCutOffTime(value);
  };
  const registrationOpenCalendar = useMemo(() => {
    if (registrationOpenOption.value === 0) {
      // "Date" option - show date picker
      return (
        <StateDayPicker
          onChange={(value) => handleDateChange(value, "open")}
          date={registrationOpenDate ? DateTime.fromISO(registrationOpenDate).toJSDate() : new Date()}
          openToDate={registrationOpenDate === null ? new Date() : null}
          disabled={!canEditRegistrationOpenDate}
        />
      );
    }
    // "Rolling" option - show number input and time unit selector
    return (
      <div className={styles.pricing_row}>
        <div className={`${styles.pricing_item} ${styles.no_padding}`}>
          <FormInput
            tagTitle={null}
            fieldName="rollingDaysBeforeOpen"
            defaultValue={rollingDaysBeforeOpen}
            onlyInteger={true}
            isEditable={canEditRegistrationOpenDate}
            hideLabel={true}
          />
        </div>
        <div className={styles.pricing_item}>
          <SelectBox
            options={rolling_time_units}
            value={rollingTimeUnitOpen}
            setValue={(value) => {
              setRollingTimeUnitOpen(value);
              setFormErrors((prev) => ({ ...prev, rollingTimeUnitOpen: false }));
            }}
            customColor="#e6e6e6"
            containerStyle={styles.no_padding}
            customControl={{
              height: "40px",
              borderRadius: "8px",
            }}
            isDisabled={!canEditRegistrationOpenDate}
          />
        </div>
      </div>
    );
  }, [registrationOpenDate, canEditRegistrationOpenDate, registrationOpenOption, rollingDaysBeforeOpen, rollingTimeUnitOpen]);

  const registrationCloseCalendar = useMemo(() => {
    if (registrationCloseOption.value === 0) {
      // "Date" option - show date picker
      return (
        <StateDayPicker
          onChange={(value) => handleDateChange(value, "close")}
          date={registrationCloseDate ? DateTime.fromISO(registrationCloseDate).toJSDate() : new Date()}
          openToDate={registrationCloseDate === null ? new Date() : null}
          disabled={!canEditRegistrationCloseDate}
          minDate={registrationCloseMinDate}
        />
      );
    }
    // "Rolling" option - show number input and time unit selector
    return (
      <div className={styles.pricing_row}>
        <div className={`${styles.pricing_item} ${styles.no_padding}`}>
          <FormInput
            tagTitle={null}
            fieldName="rollingDaysBeforeClose"
            defaultValue={rollingDaysBeforeClose}
            onlyInteger={true}
            isEditable={canEditRegistrationCloseDate}
            hideLabel={true}
          />
        </div>
        <div className={styles.pricing_item}>
          <SelectBox
            options={rolling_time_units}
            value={rollingTimeUnitClose}
            setValue={(value) => {
              setRollingTimeUnitClose(value);
              setFormErrors((prev) => ({ ...prev, rollingTimeUnitClose: false }));
            }}
            customColor="#e6e6e6"
            containerStyle={styles.no_padding}
            customControl={{
              height: "40px",
              borderRadius: "8px",
            }}
            isDisabled={!canEditRegistrationCloseDate}
          />
        </div>
      </div>
    );
  }, [registrationCloseDate, canEditRegistrationCloseDate, registrationCloseMinDate, registrationCloseOption, rollingDaysBeforeClose, rollingTimeUnitClose]);

  // CN-2564: a registration date before today is almost always a typo (the
  // wrong year), and it leaves a program that is closed the moment it is saved.
  // When editing, the date the program already has is accepted as is: an open
  // program's open date is legitimately in the past. A clone is a new program,
  // so its copied dates are checked like any other.
  const isNewPastDay = useCallback((value: string | null, saved: string | null) => {
    if (!value) {
      return false;
    }
    const day = DateTime.fromISO(value).toISODate();
    if (pricingState.edit && saved && DateTime.fromISO(saved).toISODate() === day) {
      return false;
    }
    return day < DateTime.local().toISODate();
  }, [pricingState.edit]);

  const checkErrors = useCallback(() => {
    let hasErrors = false;
    const newErrors = {
      registrationOpenTime: false,
      registrationCloseTime: false,
      registrationOpenDate: false,
      registrationCloseDate: false,
      registrationOpenDatePast: false,
      registrationCloseDatePast: false,
      start_time: false,
      static: false,
      rollingDaysBeforeOpen: false,
      rollingTimeUnitOpen: false,
      rollingDaysBeforeClose: false,
      rollingTimeUnitClose: false,
    };

    if (registrationOpenOption.value === 0) {
      if (registrationOpenTime === null) {
        newErrors.registrationOpenTime = true;
        hasErrors = true;
      }
      if (registrationOpenDate === null) {
        newErrors.registrationOpenDate = true;
        hasErrors = true;
      } else if (isNewPastDay(registrationOpenDate, pricingState.registrationOpenDate)) {
        newErrors.registrationOpenDatePast = true;
        hasErrors = true;
      }
    }

    if (registrationCloseOption.value === 0) {
      if (registrationCloseTime === null) {
        newErrors.registrationCloseTime = true;
        hasErrors = true;
      }
      if (registrationCloseDate === null) {
        newErrors.registrationCloseDate = true;
        hasErrors = true;
      } else if (isNewPastDay(registrationCloseDate, pricingState.registrationCloseDate)) {
        newErrors.registrationCloseDatePast = true;
        hasErrors = true;
      }
    }

    if (cutOffDay === null) {
      newErrors.start_time = true;
      hasErrors = true;
    }
    if (cancellationOption.value === 1 && cutOffTime === null) {
      newErrors.static = true;
      hasErrors = true;
    }

    // Validate rolling registration open fields (only when rolling option selected)
    if (registrationOpenOption.value === 1) {
      const numOpen = parseInt(rollingDaysBeforeOpen, 10);
      if (isNaN(numOpen) || !rollingDaysBeforeOpen || numOpen <= 0) {
        newErrors.rollingDaysBeforeOpen = true;
        hasErrors = true;
      }
      if (!rollingTimeUnitOpen || !rollingTimeUnitOpen.value) {
        newErrors.rollingTimeUnitOpen = true;
        hasErrors = true;
      }
    }

    // Validate rolling registration close fields (only when rolling option selected)
    if (registrationCloseOption.value === 1) {
      const numClose = parseInt(rollingDaysBeforeClose, 10);
      if (isNaN(numClose) || !rollingDaysBeforeClose || numClose <= 0) {
        newErrors.rollingDaysBeforeClose = true;
        hasErrors = true;
      }
      if (!rollingTimeUnitClose || !rollingTimeUnitClose.value) {
        newErrors.rollingTimeUnitClose = true;
        hasErrors = true;
      }
    }

    setFormErrors(newErrors);

    return hasErrors;
  }, [
    registrationCloseDate,
    registrationCloseTime,
    registrationOpenDate,
    registrationOpenTime,
    isNewPastDay,
    pricingState.registrationOpenDate,
    pricingState.registrationCloseDate,
    cutOffDay,
    cutOffTime,
    cancellationOption,
    registrationOpenOption,
    registrationCloseOption,
    rollingDaysBeforeOpen,
    rollingTimeUnitOpen,
    rollingDaysBeforeClose,
    rollingTimeUnitClose,
  ]);

  useEffect(() => {
    if (!isEmpty(methods.formState.errors)) {
      checkErrors();
    }
  }, [methods.formState.errors, checkErrors]);

  const constructDate = (date, hour) => {
    let newDate = DateTime.fromISO(date).setZone(pricingState.timeZone, {
      keepLocalTime: true,
    });

    const [hours, minutes] = hour.split(":");
    newDate = newDate.set({
      hour: parseInt(hours, 10),
      minute: parseInt(minutes, 10),
    });
    return newDate.toISO();
  };

  const checkRegistrationErrors = () => {
    const openIsRolling = registrationOpenOption.value === 1;
    const closeIsRolling = registrationCloseOption.value === 1;
    const toDays = (count, unit) => (unit === "weeks" ? count * 7 : count);

    if (openIsRolling && closeIsRolling) {
      const openDays = toDays(parseInt(rollingDaysBeforeOpen, 10), rollingTimeUnitOpen.value);
      const closeDays = toDays(parseInt(rollingDaysBeforeClose, 10), rollingTimeUnitClose.value);
      if (openDays <= closeDays) {
        setShowErrorModal(true);
        return true;
      }
      return false;
    }

    if (openIsRolling || closeIsRolling) {
      const eventStartISO = constructDate(pricingState.date, pricingState.startTime?.value);
      if (!eventStartISO) {
        return false;
      }
      const eventStart = DateTime.fromISO(eventStartISO);

      const openAt = openIsRolling
        ? eventStart.minus({
            days: toDays(parseInt(rollingDaysBeforeOpen, 10), rollingTimeUnitOpen.value),
          })
        : DateTime.fromISO(constructDate(registrationOpenDate, registrationOpenTime?.value));
      const closeAt = closeIsRolling
        ? eventStart.minus({
            days: toDays(parseInt(rollingDaysBeforeClose, 10), rollingTimeUnitClose.value),
          })
        : DateTime.fromISO(constructDate(registrationCloseDate, registrationCloseTime?.value));

      if (!openAt.isValid || !closeAt.isValid) {
        return false;
      }
      if (closeAt <= openAt) {
        setShowErrorModal(true);
        return true;
      }
      return false;
    }

    if (
      registrationCloseDate < registrationOpenDate ||
      (registrationCloseDate === registrationOpenDate &&
        registrationCloseTime?.value <= registrationOpenTime?.value)
    ) {
      setShowErrorModal(true);
      return true;
    }
    return false;
  };

  const selectURL = (stateURL, newURL) => {
    if (stateURL?.substring(0, 5) === "blob:" || !stateURL) {
      return newURL;
    } else {
      return stateURL;
    }
  };

  const handleBack = () => {
    dispatch(setPage(2));
  };

  const onSubmit = async (data) => {
    // Clear previous server errors
    setServerErrors({});

    const cancellationTime =
      cancellationOption.value === 0
        ? cutOffDay.value
        : 24 * cutOffDay.value + cutOffTime.value;
    const externalErrors = checkErrors();
    if (externalErrors) {
      return;
    }
    const registrationErrors = checkRegistrationErrors();
    if (registrationErrors) {
      return;
    }

    setLoading(true);

    let event = {
      start_time: constructDate(
        pricingState.date,
        pricingState.startTime?.value,
      ),
      end_time: constructDate(pricingState.date, pricingState.endTime?.value),
      repeat: null,
      until_date: null,
    };
    if (
      pricingState.repeat?.value &&
      typeof pricingState.repeat.value === "string" &&
      pricingState.repeat.value !== "Never"
    ) {
      event = { ...event, repeat: pricingState.repeat.value.toLowerCase() };
    }
    if (pricingState.untilDate) {
      event = {
        ...event,
        until_date: DateTime.fromISO(pricingState.untilDate.toString())
          .setZone("utc", {
            keepLocalTime: true,
          })
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .toISO(),
      };
    }

    const newTags = pricingState.tags
      ?.map((item) => item?.__isNew__ && item.label)
      .filter((item) => item);

    const selectedTags = pricingState.tags
      ?.map((item) => !item?.__isNew__ && item.value)
      .filter((item) => item);

    let thumbnail_url: string = "";
    let header_url: string = "";
    let flyer_pdf_url: string = "";
    if (thumbnail && thumbnailChanged) {
      const uploadResult = await upload(
        thumbnail,
        thumbnail.name,
        thumbnail.type,
        undefined,
      );
      thumbnail_url = uploadResult.url;
    }
    if (header && headerChanged) {
      const uploadResult = await upload(
        header,
        header.name,
        header.type,
        undefined,
      );
      header_url = uploadResult.url;
    }
    if (flyer && flyerChanged) {
      const uploadResult = await upload(
        flyer,
        flyer.name,
        flyer.type,
        undefined,
      );
      flyer_pdf_url = uploadResult.url;
    }

    const collect_type =
      collectOnline && collectOffline ? "both" :
        collectOnline ? "online" : "offline";

    const body = {
      attendance: pricingState.type.toLowerCase(),
      category: pricingState.category,
      title: pricingState.title,
      subtitle: pricingState.subtitle,
      event,
      location: pricingState.location,
      email: pricingState.email,
      phone: pricingState.phone,
      short_description: pricingState.shortDescription,
      long_description: pricingState.longDescription,
      program_tag_ids: selectedTags,
      new_tag_names: newTags,
      coach_ids: pricingState.coachIds,
      price: isFree ? 0 : data.price,
      is_free: isFree,
      capacity: parseInt(data.capacity, 10),
      with_waitlist: attendanceOption?.value === 0 ? true : false,
      registration_start_time: registrationOpenOption?.value === 0 ? constructDate(
        registrationOpenDate,
        registrationOpenTime?.value,
      ) : {
        time_count: data.rollingDaysBeforeOpen,
        time_unit: rollingTimeUnitOpen.value
      },
      registration_end_time: registrationCloseOption?.value === 0 ? constructDate(
        registrationCloseDate,
        registrationCloseTime?.value,
      ) : {
        time_count: data.rollingDaysBeforeClose,
        time_unit: rollingTimeUnitClose.value
      },
      cancellation_cut_off_time: cancellationTime,
      cancellation_cut_off_type:
        cancellationOption.value === 0 ? "start_time" : "static",
      privacy: pricingState.privacy,
      restrict_additional_people: pricingState.restrictAdditionalPeople,
      external_thumbnail_url: selectURL(
        pricingState.thumbnailURL,
        thumbnail_url,
      ),
      external_header_url: selectURL(pricingState.headerURL, header_url),
      flyer_pdf_url: pricingState.flyerURL === null ? null : selectURL(pricingState.flyerURL, flyer_pdf_url),
      collect_type,
      online_default: isOnlineDefault,
      payment_collector_coach_profile_id: data.stripe_account_id,
      bulk_create: pricingState?.bulkCreate,
      ...(pricingState?.customRecurrence && pricingState.repeat?.value === "custom" ? {
        custom_recurrence: {
          interval: pricingState.customRecurrence.interval,
          frequency: pricingState.customRecurrence.frequency,
          days_of_week: pricingState.customRecurrence.daysOfWeek,
          ...(pricingState.customRecurrence.occurrences ? { occurrences: pricingState.customRecurrence.occurrences } : {}),
        }
      } : {}),
    };

    pricingState.edit
      ? schedulingFetchPatch(`/programs/${pricingState.id}`, body)
          .then(() => {
            history.push("/programs");
          })
          .catch((error) => {
            setLoading(false);

            // Parse field-specific errors
            const fieldErrors: Record<string, string> = {};
            if (error.errors && Array.isArray(error.errors)) {
              error.errors.forEach((err) => {
                const fieldName = err.source?.parameter;
                const message = err.details?.[0]?.message;
                if (fieldName && message) {
                  fieldErrors[fieldName] = message;
                }
              });
            }

            // If we have field-specific errors, show them on fields
            if (Object.keys(fieldErrors).length > 0) {
              setServerErrors(fieldErrors);
            } else {
              // Otherwise show general error modal
              setApiError(true);
              if (error.errors?.[0]?.details) {
                setApiErrorText(error.errors[0].details[0].message?.text);
              } else {
                setApiErrorText(error.errors?.[0]?.detail);
              }
            }
          })
      : schedulingFetchPost(`/programs`, body)
          .then(() => {
            history.push("/programs");
          })
          .catch((error) => {
            setLoading(false);

            // Parse field-specific errors
            const fieldErrors: Record<string, string> = {};
            if (error.errors && Array.isArray(error.errors)) {
              error.errors.forEach((err) => {
                const fieldName = err.source?.parameter;
                const message = err.details?.[0]?.message;
                if (fieldName && message) {
                  fieldErrors[fieldName] = message;
                }
              });
            }

            // If we have field-specific errors, show them on fields
            if (Object.keys(fieldErrors).length > 0) {
              setServerErrors(fieldErrors);
            } else {
              // Otherwise show general error modal
              setApiError(true);
              if (error.errors?.[0]?.details) {
                setApiErrorText(error.errors[0].details[0].message?.text);
              } else {
                setApiErrorText(error.errors?.[0]?.detail);
              }
            }
          });
  };

  const cutOffExtraOptions = useMemo(() => {
    const extraSpace = cancellationOption?.value === 1;
    return (
      <div className={extraSpace ? styles.pricing_row_with_gap : ""}>
        <div className={extraSpace ? styles.pricing_item : ""}>
          <SelectBox
            options={extraSpace ? cut_off_days : cut_off_list}
            value={cutOffDay}
            setValue={(selection) => handleCutOffChange(selection, "static")}
            containerStyle={styles.mt_8}
            customColor="#e6e6e6"
            isDisabled={!canEditRegistrationSettings}
          />
        </div>
        {cancellationOption?.value === 1 && (
          <div className={styles.pricing_item}>
            <SelectBox
              options={dayHours(timeFormat)}
              value={cutOffTime}
              setValue={(selection) => handleCutOffChange(selection, "time")}
              containerStyle={styles.mt_8}
              customColor="#e6e6e6"
              isDisabled={!canEditRegistrationSettings}
            />
          </div>
        )}
      </div>
    );
  }, [cancellationOption, cutOffDay, cutOffTime, cut_off_days, cut_off_list, canEditRegistrationSettings]);

  const customStyles: StylesConfig = {
    control: (base: any) => ({
      ...base,
      background: 'white',
      border: '1px solid #ccc',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      color: 'black',
      fontWeight: '400',
      backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
      cursor: 'pointer',
    }),
    multiValue: (styles: any) => ({
      ...styles,
      backgroundColor: '#27a4ff',
      padding: '4px',
      borderRadius: '4px',
    }),
    multiValueLabel: (styles: any) => ({
      ...styles,
      color: 'white',
      fontSize: '16px',
    }),
    multiValueRemove: (styles: any) => ({
      ...styles,
      color: 'white',
      ':hover': {
        color: 'white',
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'black',
    }),
    container: (provided: any) => ({
      ...provided,
      color: '#8C8C8C',
    }),
    placeholder: (defaultStyles: any) => ({
      ...defaultStyles,
      color: '#8c8c8c',
    }),
  };

  const durationCustomStyles: StylesConfig = {
    ...customStyles,
    valueContainer: (base: any) => ({
      ...base,
      height: '46px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: () => ({
      height: '42px',
    }),
    menuList: () => ({
      cursor: 'pointer'
    })
  };

  // Shared between the paid section and the free-clinic section: free clinics
  // collect no payment but still record $0 registrations under a chosen coach.
  // collectOnline is forced off for free so no Stripe-account validation applies.
  const renderPaymentCollector = () => {
    if (hasFacilityLevelStripe) {
      return null;
    }

    if (canEditPaymentCollector) {
      return (
        <StripeAccountSelector
          control={methods.control}
          stripeCoachOptions={allCoachOptions}
          stripeEnabledCoaches={stripeEnabledCoaches}
          collectOnline={isFree ? false : collectOnline}
          canEditPaymentCollector={canEditPaymentCollector}
          durationCustomStyles={durationCustomStyles}
          isFree={isFree}
        />
      );
    }

    if (pricingState.paymentCollectorCoachProfileId) {
      return (
        <div className={styles.readOnlyCollector}>
          <span className={styles.gray_text}>{t("Payment Collector (Locked)")}</span>
          <div className={styles.readOnlyValue}>
            {allCoachOptions.find((c) => c.id === pricingState.paymentCollectorCoachProfileId)?.label || t("Unknown")}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={`${styles.container} programs-page-container`}
    >
      {showErrorModal && (
        <InformationModal
          topImage={CalendarX}
          handleAction={() => setShowErrorModal(false)}
          title={t("Error")}
          subtitle={t(
            "The Registration Closes cannot be set prior to the Registration Opens.",
          )}
          width="500px"
        />
      )}
      {apiError && (
        <InformationModal
          topImage={CalendarX}
          handleAction={() => setApiError(false)}
          title={t("Error")}
          subtitle={apiErrorText}
          width="400px"
        />
      )}
      <FormProvider {...methods}>
        <form id="programForm">
          <div className={styles.pricing_container}>
            <div className={`${utilStyles.column} ${styles.pricing_section}`}>
              <label className={styles.gray_text}>
                {t("Reservation Policy")}
              </label>
              <div className={styles.horizontal_line} />
              <div className={styles.column}>
                <div className={styles.pricing_item}>
                  <FormInput
                    tagTitle={t("Capacity")}
                    placeholder={t("Max Number of Athletes")}
                    fieldName="capacity"
                    isNumberOnly={true}
                    isRequired={true}
                    error={serverErrors.capacity}
                  />
                </div>
                <div className={`${styles.pricing_item} `}>
                  <Label
                    text={t("Waitlist")}
                    required={true}
                    item={(
                      <ButtonsContainer
                        customStyle={styles.pricing_item}
                        items={attendance_buttons()}
                        containerStyle={styles.option_button_group}
                        openCalendarSelected={attendanceOption}
                        setOpenCalendarSelected={setAttendanceOption}
                        disabled={!canEditRegistrationSettings}
                      />
                    )}
                    containerStyle={styles.label_container}
                  />
                </div>
                <Label
                  containerStyle={styles.pricing_item}
                  text={t('Registration Open')}
                  required={true}
                  item={(
                    <ButtonsContainer
                      customStyle={styles.pricing_item}
                      items={registration_open_buttons()}
                      containerStyle={styles.option_button_group}
                      openCalendarSelected={registrationOpenOption}
                      setOpenCalendarSelected={setRegistrationOpenOption}
                      disabled={!canEditRegistrationOpenDate}
                    />
                  )}
                />
                <div className={`${styles.pricing_row} `}>
                  <div className={styles.pricing_item}>
                    <Label
                      text={t("Registration Opens")}
                      required={true}
                      item={registrationOpenCalendar}
                      error={
                        registrationOpenOption.value === 0
                          ? (formErrors.registrationOpenDate && errorMessage) ||
                            (formErrors.registrationOpenDatePast && t("Registration can't open on a past date"))
                          : formErrors.rollingDaysBeforeOpen && errorMessage
                      }
                    />
                  </div>
                  {registrationOpenOption.value === 0 &&(
                    <div className={styles.pricing_item}>
                      <Label
                        text={t("Time")}
                        required={true}
                        error={formErrors.registrationOpenTime && errorMessage}
                        item={(
                          <SelectBox
                            options={hoursWithLables}
                            value={registrationOpenTime}
                            setValue={(time) =>
                              handleTimeChange(time, "registrationOpen")
                            }
                            customColor="#e6e6e6"
                            containerStyle={styles.no_padding}
                            customControl={{
                              height: "40px",
                              borderRadius: "8px",
                            }}
                            isDisabled={!canEditRegistrationOpenDate}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
                <Label
                  containerStyle={styles.pricing_item}
                  text={t('Registration Close')}
                  required={true}
                  item={(
                    <ButtonsContainer
                      customStyle={styles.pricing_item}
                      items={registration_close_buttons()}
                      containerStyle={styles.option_button_group}
                      openCalendarSelected={registrationCloseOption}
                      setOpenCalendarSelected={setRegistrationCloseOption}
                    />
                  )}
                />
                <div className={`${styles.pricing_row} `}>
                  <div className={styles.pricing_item}>
                    <Label
                      text={t("Registration Closes")}
                      required={true}
                      error={
                        registrationCloseOption.value === 0
                          ? (formErrors.registrationCloseDate && errorMessage) ||
                            (formErrors.registrationCloseDatePast && t("Registration can't close on a past date"))
                          : formErrors.rollingDaysBeforeClose && errorMessage
                      }
                      item={registrationCloseCalendar}
                    />
                  </div>
                  {registrationCloseOption.value === 0 && (
                    <div className={styles.pricing_item}>
                      <Label
                        text={t("Time")}
                        required={true}
                        error={formErrors.registrationCloseTime && errorMessage}
                        item={(
                          <SelectBox
                            options={hoursWithLables}
                            value={registrationCloseTime}
                            setValue={(time) =>
                              handleTimeChange(time, "registrationClose")
                            }
                            customColor="#e6e6e6"
                            containerStyle={styles.no_padding}
                            customControl={{
                              height: "40px",
                              borderRadius: "8px",
                            }}
                            isDisabled={!canEditRegistrationCloseDate}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
                <div className={`${styles.pricing_row} `}>
                  <div className={styles.pricing_item}>
                    <Label
                      text={t("Cancellation Cut-off Time")}
                      required={true}
                      item={(
                        <ButtonsContainer
                          customStyle={styles.pricing_item}
                          items={cancellation_buttons()}
                          containerStyle={styles.option_button_group}
                          openCalendarSelected={cancellationOption}
                          setOpenCalendarSelected={handleCutOffButtonChange}
                          disabled={!canEditRegistrationSettings}
                        />
                      )}
                      containerStyle={styles.label_container}
                    />
                  </div>
                  <div className={styles.pricing_item}>
                    <Label
                      error={
                        cancellationOption.value === 0
                          ? formErrors.start_time && errorMessage
                          : formErrors.static && errorMessage
                      }
                      text={cancellationOption?.label}
                      required={true}
                      item={cutOffExtraOptions}
                      containerStyle={styles.no_padding}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.pricing_vertical_line} />
            <div className={`${utilStyles.column} ${styles.contact_section}`}>
              <label className={styles.gray_text}>{t("Pricing")}</label>
              <div className={styles.horizontal_line} />
              {FREE_CLINICS_ENABLED && (
                <div className={`${utilStyles.column} ${styles.free_clinic_toggle}`}>
                  <SwitchToggle
                    checked={isFree}
                    onChange={setIsFree}
                    title={t("Free Clinic")}
                    disabled={!canEditRegistrationSettings}
                  />
                </div>
              )}
              {!isFree && (
                <>
                  <div className={utilStyles.column}>
                    <FormInput
                      tagTitle={generatePriceLabel(pricingState?.currency, pricingState?.type, t)}
                      fieldName="price"
                      isRequired={true}
                      addDollar={true}
                      currency={{ currency: currencyLabel }}
                      onlyInteger={false}
                      isEditable={canEditRegistrationSettings}
                      error={serverErrors.price}
                    />
                  </div>
                  <div className={utilStyles.column}>
                    <PaymentMethodSelector
                      collectOnline={collectOnline}
                      setCollectOnline={setCollectOnline}
                      collectOffline={collectOffline}
                      setCollectOffline={setCollectOffline}
                      onlineDefault={onlineDefault}
                      setOnlineDefault={setOnlineDefault}
                      disabled={!canEditPaymentMethod}
                      tracking={paymentTracking}
                    />

                    {renderPaymentCollector()}
                  </div>
                </>
              )}
              {/* Free clinics collect no payment, but the creator still picks the
                  coach the $0 registrations are recorded under. */}
              {isFree && (
                <div className={utilStyles.column}>
                  {renderPaymentCollector()}
                </div>
              )}
            </div>
          </div>
        </form>
        <SaveBar
          submitDisabled={loading}
          onCancel={handleBack}
          onSubmit={methods.handleSubmit(onSubmit)}
          cancelLabel={t("Back")}
          showCancel
          submitLabel={loading ? t("Saving") + "..." : t("Save")}
        />
      </FormProvider>
    </div>
  );
};

export default PricingOptions;
